import * as React from 'react';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import mime from 'mime-types';
import {
  ColDef,
  GridApi,
  GridOptions,
  RowClickedEvent,
  ValueFormatterParams
} from 'ag-grid-community';
import {
  Box,
  Flex,
  Heading,
  useDisclosure,
  Button,
  Table,
  TableContainer,
  Tbody,
  Text,
  Link,
  Grid,
  GridItem,
  Tooltip,
  Spinner,
  Image,
  Textarea,
  Select,
  Menu,
  MenuButton,
  MenuList,
  MenuItem
} from '@chakra-ui/react';
import LicenseReviewDataSource from '../ducks/licenseReviewDatasource';
import { getFormattedDate, getFormattedDateTime, getFormattedDateTimeFroManualReviewedAt, getFormattedDateTimeFromCreatedAt, getFormattedDateTimeFromReviewedAt } from '@/queue/elements/value-formatters/date-formatter.mjs';
import { LicenseConfirmation, LicenseReview, ReviewConfirmation } from '@/model/license-review.mjs';
import TableElement from '@/table/TableElement';
import HyperLink from '@/hyperlink/hyperlink';
import s3Utils from '../../utils/s3Utils';
import FFLDetailModal from '@/explore/ffl-detail/FFLDetailModal';
import { getIsGlobalAdmin } from '@/redux/app-selectors';
import AppConfig from '@/utils/app-config.mjs';
import { SERVER_PAGE_LICENSE_APPROVAL_LIST_SIZE } from '@/model/Constants';
import { agGridIconsConfig } from '@/explore/ffl-detail/utils.mjs';
import ControlledInput from '@/common/ControllerInput';
import { toggleCertificateMarkedAsReviewedStatus, updateLicenseApprovalStatus } from '@/api/apis';
import ModalComponent from '@/common/Modal';
import { licenseApprovalValidation, regexFullLicense, textareaLengthValidation } from '@/queue/ducks/validationSchema';
import PreviewLicense, { ShowPreview } from '@/common/PreviewLicense';
import ConfirmationModal from '@/common/ConfirmationModal';
import { normalizeRejectedReasonFromApi, reviewFilters, reviewRejectedReasonsFflLoa, SOURCE_OF_LICENSE, stepMapping } from '@/utils/constants';
//@ts-ignore
import InputMask from 'react-input-mask';
import { getHideUnnecessaryRejectionValue, getLicenseApprovalFilterModel, getLicenseApprovalSort } from './selector';
import ReviewConfirmationModal from '@/common/ReviewConfirmationModal';
import { fixTimezoneOffset, trimDate } from '@/queue/utils'
import { MapInteractionCSS } from "react-map-interaction";
import { useSearchParams } from 'react-router-dom';
import { setIsCertificateStatusUpdated, setIsRefreshingLOAApproval, setLicenseApprovalFilterModel, setLicenseApprovalSearchText } from '../ducks/slice';
import { useDispatch } from 'react-redux';
import InvalidCertificateModal from '@/common/InvalidCertificateModal';
import { getFormattedTextForManualReview } from '@/utils/utils';
import { callToGetDocumentStatus, callToGetLicenseRecordDetails, setDocumentsRequiredDetails, setValidLicenseRecord } from '@/queue/ducks/slice';
import { getDocumentsRequiredDetails, getIsDealerDetailsLoading, getValidLicenseRecord } from '@/queue/ducks/selectors';
import FlatDatePickr from '@/common/Flatpickr';
import LicenseDocumentsGridView, {
  type DocumentCardHoverReviewKind,
  type DocumentGridRotateKind
} from './documents-grid-view';
import {
  isRasterImageForBrowserRotation,
  normalizeStorageKeyFromApi,
  rotateRasterImageBlob,
  uploadRotatedRasterToLicenseReader
} from '@/utils/rotateImageAndUploadS3';

/** Survives full page reload so gallery + preview can skip cached HTTP responses after same-key S3 overwrite. */
const LICENSE_GALLERY_THUMB_S3_BUST_KEY =
  'ffl_bo_license_gallery_thumb_s3_overwrite_v1';

function readLicenseGalleryThumbS3BustMap(): Record<string, number> {
  try {
    if (typeof sessionStorage === 'undefined') return {};
    const raw = sessionStorage.getItem(LICENSE_GALLERY_THUMB_S3_BUST_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return {};
    const out: Record<string, number> = {};
    for (const [docId, v] of Object.entries(parsed as Record<string, unknown>)) {
      const n = typeof v === 'number' ? v : Number(v);
      if (docId && !Number.isNaN(n) && n >= 1) out[docId] = n;
    }
    return out;
  } catch {
    return {};
  }
}

function persistLicenseGalleryThumbS3Bust(docId: string, nonce: number) {
  try {
    if (typeof sessionStorage === 'undefined') return;
    const map = readLicenseGalleryThumbS3BustMap();
    map[docId] = Math.max(map[docId] ?? 0, nonce);
    sessionStorage.setItem(
      LICENSE_GALLERY_THUMB_S3_BUST_KEY,
      JSON.stringify(map)
    );
  } catch {
    /* quota / disabled */
  }
}

interface props {
  doRefresh: number;
  /** Incremented from parent when header Refresh is clicked (refetches gallery when table grid is unmounted). */
  toolbarRefreshNonce: number;
  tabName: string | undefined;
  className: string;
  gridRef: React.RefObject<AgGridReact<LicenseReview>>;
}

interface Props {
  valueFormatted: string;
  value: string;
  
}

/** Paths match `public/images/ui/toggle-table-view.svg` (stroke varies by state). */
function LicenseTableViewIcon({ color }: { color: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={14}
      height={14}
      viewBox="0 0 14 14"
      fill="none"
      aria-hidden
    >
      <path
        d="M5.25 1.75H2.91667C2.60725 1.75 2.3105 1.87292 2.09171 2.09171C1.87292 2.3105 1.75 2.60725 1.75 2.91667V5.25M5.25 1.75H11.0833C11.3928 1.75 11.6895 1.87292 11.9083 2.09171C12.1271 2.3105 12.25 2.60725 12.25 2.91667V5.25M5.25 1.75V12.25M1.75 5.25V11.0833C1.75 11.3928 1.87292 11.6895 2.09171 11.9083C2.3105 12.1271 2.60725 12.25 2.91667 12.25H5.25M1.75 5.25H12.25M12.25 5.25V11.0833C12.25 11.3928 12.1271 11.6895 11.9083 11.9083C11.6895 12.1271 11.3928 12.25 11.0833 12.25H5.25"
        stroke={color}
        strokeWidth="1.33333"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Paths match `public/images/ui/toggle-gallery-view.svg` (single stroke color per state). */
function LicenseGalleryViewIcon({ color }: { color: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={14}
      height={14}
      viewBox="0 0 14 14"
      fill="none"
      aria-hidden
    >
      <path
        d="M7 1.75V12.25"
        stroke={color}
        strokeWidth="1.33"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M1.75 7H12.25"
        stroke={color}
        strokeWidth="1.33"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M11.0833 1.75H2.91667C2.27233 1.75 1.75 2.27233 1.75 2.91667V11.0833C1.75 11.7277 2.27233 12.25 2.91667 12.25H11.0833C11.7277 12.25 12.25 11.7277 12.25 11.0833V2.91667C12.25 2.27233 11.7277 1.75 11.0833 1.75Z"
        stroke={color}
        strokeWidth="1.33"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function LicenseReviewTable({
  doRefresh,
  toolbarRefreshNonce,
  tabName,
  className,
  gridRef,
}: props) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isLicenseModalOpen,
    onOpen: onLicenseModalOpen,
    onClose: onLicenseModalClose
  } = useDisclosure();
  const {
    isOpen: isHyperLinkOpen,
    onOpen: onHyperLinkOpen,
    onClose: onHyperLinkClose
  } = useDisclosure();
  const [licenseReview, setLicenseReview] = useState<LicenseReview | null>(
    null
  );
  const [initialLicenseData, setInitialLicenseData] =
    useState<LicenseReview | null>(null);
  const {
    isOpen: isLicensePreviewOpen,
    onOpen: onLicensePreviewOpen,
    onClose: onLicensePreviewClose
  } = useDisclosure();
  const {
    isOpen: isReviewConfirmationMadalOpen,
    onClose: onCloseReviewConfirmationModal,
    onOpen: onOpenReviewConfirmationModal
  } = useDisclosure();
  const {
    isOpen: isInvalidCertificateMadalOpen,
    onClose: onClosInvalidCertificateModal,
    onOpen: onOpenInvalidCertificateModal
  } = useDisclosure();
  const [licenseeId, setLicenseeId] = useState<string>('');
  const [ocrExpDate, setOcrExpDate] = useState<any>(null);
  const [signatureDate, setSignatureDate] = useState<any>(null);
  const [downloadingFile1, setDownloadingFile1] = useState<boolean>(false);
  const [downloadingFile2, setDownloadingFile2] = useState<boolean>(false);
  const dataSource = useMemo(() => new LicenseReviewDataSource(), []);
  const blob = new Blob();
  const [licenseBlob, setLicenseBlob] = useState<Blob | null>(blob);
  const [imageLoadError, setImageLoadError] = useState<boolean>(false);
  const [reviewConfirmation, setReviewConfirmation] = useState<ReviewConfirmation>({
    header: "",
    isCancelBtnDisable: false,
    isConfirmBtnDisable: false,
    isConfirmBtnLoading: false,
    isSaveBtnLoading: false,
    rejectedReason: "",
    note: "",
    error: "",
    status: "",
  });
  const [reviewInputVal, setReviewInputVal] = useState<any>({
    rejectReason: "",
    note: "",
  });
  const [licenseListView, setLicenseListView] = useState<'table' | 'gallery'>(
    'table'
  );
  const [documentsGalleryRefreshKey, setDocumentsGalleryRefreshKey] =
    useState(0);
  /**
   * After rotate: `blob:` preview (instant) then S3 object key when upload finishes.
   * Caller revokes blob URLs when replaced or on unmount.
   */
  const [galleryImagePathOverrideByDocId, setGalleryImagePathOverrideByDocId] =
    useState<Record<string, string>>({});
  const [galleryThumbnailReloadNonceByDocId, setGalleryThumbnailReloadNonceByDocId] =
    useState<Record<string, number>>(readLicenseGalleryThumbS3BustMap);
  const isLicenseReviewModalOpenRef = useRef(false);
  const licenseReviewRowIdRef = useRef<string | null>(null);
  /** Rotated blob used for modal when S3 upload not done yet (not a `blob:` string on the row). */
  const pendingRotatedBlobByDocIdRef = useRef<Map<string, Blob>>(new Map());
  /** Bumped on each rotate start so stale in-flight uploads do not overwrite UI. */
  const rotateGenerationByDocIdRef = useRef<Record<string, number>>({});
  const galleryOverrideSnapshotRef = useRef(galleryImagePathOverrideByDocId);
  galleryOverrideSnapshotRef.current = galleryImagePathOverrideByDocId;
  useEffect(() => {
    isLicenseReviewModalOpenRef.current = isOpen;
  }, [isOpen]);
  useEffect(() => {
    licenseReviewRowIdRef.current = licenseReview?.id ?? null;
  }, [licenseReview?.id]);
  useEffect(
    () => () => {
      Object.values(galleryOverrideSnapshotRef.current).forEach((v) => {
        if (v && /^blob:/i.test(v)) {
          URL.revokeObjectURL(v);
        }
      });
    },
    []
  );
  const [invalidCertificate, setInvalidCertificate] = useState<any>({
    header: "",
    note: "The certificate provided appears to be either invalid or corrupted, so unfortunately, we are unable to approve it.",
  });
  const [textareaEvent, setTextareaEvent] = useState<any>(null);
  const licenseApprovalFilterModel = getLicenseApprovalFilterModel();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const licenseApprovalSort = getLicenseApprovalSort();
  const hideUnnecessaryRejectionValue = getHideUnnecessaryRejectionValue()


  const premiseAddress = useRef(null) as unknown as React.MutableRefObject<HTMLInputElement>;
  const mailingAddress = useRef(null) as unknown as React.MutableRefObject<HTMLInputElement>;

  useEffect(() => {
      premiseAddress?.current?.setSelectionRange(textareaEvent, textareaEvent);
      mailingAddress?.current?.setSelectionRange(textareaEvent, textareaEvent);
  }, [licenseReview?.premise_address, licenseReview?.mailing_address])

  const handleSortByChange = React.useCallback(() => {
    if (dataSource && gridRef.current?.api) {
      // Show loading overlay immediately
      gridRef.current.api.showLoadingOverlay();
      
      // Update datasource with new sortBy value
      dataSource.setSortBy(licenseApprovalSort === 'true');
      
      // Refresh server-side data
      setTimeout(() => {
        gridRef.current?.api.refreshServerSide();
      }, 50); // Small delay to ensure loading overlay is visible
    }
  }, [licenseApprovalSort, dataSource]);

  useEffect(() => {
    handleSortByChange();
  }, [handleSortByChange]);

  useEffect(() => {
    if (doRefresh === 1) reFetchData();
  }, [doRefresh]);

  useEffect(() => {
    if (toolbarRefreshNonce > 0) {
      setDocumentsGalleryRefreshKey((k) => k + 1);
    }
  }, [toolbarRefreshNonce]);
  function renderHyperLink(data: any, columnName: string, fullLicenseKey: string | null= null) {
    let renderValue = '';
    let linkUrl = '';
    if (columnName === "full_license_key") {
      linkUrl = `/dealers-search/${data}`;
      renderValue = (data && fullLicenseKey) ? fullLicenseKey :  "NONE";
    } else if (columnName === "transfer_id") {
      linkUrl = `/transfers/${data}`;
      renderValue = data || '';
    }
    const ref = (ref: any) => {
      if (!ref) return;
      ref.onclick = (e: any) => {
        e.stopPropagation();
        window.open(linkUrl, '_blank');
      };
    };
    const text = (color: string) => (
      <Text color={color}>{renderValue}</Text>
    );
    return (!renderValue || renderValue === 'NONE') ? (
      text('black')
    ) : (
        <Box cursor={'pointer'} _hover={{textDecoration:'underline', textDecorationColor:'#0065FF', textUnderlineOffset: 2}} ref={ref}>{text('#0065FF')}</Box>
    );
  }

  const checkStatus = (currentStatus: string | undefined, status: string) => {
    return currentStatus === status;
  };

  const getStatusValue = (value: string | null) => {
    if(!value) return {color: '', value: 'NONE'};
    if(["approved"].includes(value as string)) return {color: 'green', value: 'Approved'};
    else if(["rejected"].includes(value as string)) return {color: 'red', value: 'Rejected'};
    else return {color: 'orange', value: 'Pending'};
  };

  function StatusCellRenderer({ value }: { value: string }) {
    const statusDetails = getStatusValue(value);
    return (
      <Box>
        <Box as={'span'} color={statusDetails?.color} marginRight={'0.25rem'}>
          {'\u2B24'}
        </Box>
        {statusDetails?.value}
      </Box>
    );
  }
  function ActionPerformedByCellRenderer({ data, value }:any) { 
    const actionPerformedBy: any = {
      System: 'backend',
      Agent: 'profile',
      Dealer: 'dealer',
      system: 'backend',
      agent: 'profile',
      dealer: 'dealer',
      Support: 'profile',
      support: 'profile',
      SMS: 'dealer',
      sms: 'dealer',
      email_campaign: 'email',
      ffl360: 'profile'
    }
    const email = data?.manual_review_requested_by && data?.manual_review_requested_by !==null ? `(${data?.manual_review_requested_by})` : '';
    if (value && value !== null) {
      if (value?.toLowerCase() === "email_campaign") {
        return `Via Dealer ${email}`;
      }
      if (value?.toLowerCase() === "dealer" || value?.toLowerCase() === "upload_ui") {
        return `Via Dealer ${email}` ;
      }
      if (actionPerformedBy[value]) {
        return `Via ${actionPerformedBy[value]} ${email}`;
      }
      return ''
    }
    return ''
  }
  function SourceCellRenderer({ data }: ValueFormatterParams) {
    return (
      <Box>
        {SOURCE_OF_LICENSE[data?.source] || ''}
      </Box>
    );
  }

  const getFailedStep = (value: number | null) => {
    let status: string | null = '';
    if (value) {
      status = stepMapping[value?.toString()] || '';
    }
    return status;
  };

  useEffect(()=>{
    reFetchData()
  },[hideUnnecessaryRejectionValue])

  function reFetchData() {
    gridRef.current?.api?.refreshServerSide();
    gridRef?.current?.api?.sizeColumnsToFit();
  }
  const columnDefs: ColDef[] = [
    {
      field: 'transfer_id',
      headerName: 'Transfer ID',
      cellRenderer: ({ value, valueFormatted }: Props) => renderHyperLink(value, "transfer_id"),
      width: 240,
      suppressSizeToFit: true
    },
    {
      field: 'full_license_key',
      headerName: 'License Number',
      cellRenderer: ({ data }: ValueFormatterParams<LicenseReview>) => {
        const fullLicenseKey = data?.full_license_key && data?.full_license_key.length === 20
            ? data?.full_license_key
            : 'NONE'
        return renderHyperLink(data?.dealer_id, "full_license_key", fullLicenseKey);

      },
      width: 200,
      suppressSizeToFit: true
    },
    {
      field: 'license_name',
      headerName: 'License Name',
      cellRenderer: ({ value }: Props) => value || 'NONE',
      width: 250,
      suppressSizeToFit: true
    },
    {
      field: 'business_name',
      headerName: 'Business Name',
      cellRenderer: ({ value, valueFormatted }: Props) => {
        return value || 'NONE';
      },
      width: 250,
      suppressSizeToFit: true
    },
    {
      field: 'approval_status',
      headerName: 'Status',
      filter: "agSetColumnFilter",
      cellRenderer: StatusCellRenderer,
      width: 150,
      filterParams: {
        values: reviewFilters.status.map((item: any) => item.value),
      },
      suppressSizeToFit: true
    },
    {
      field: 'reviewed_at',
      headerName: 'Review Date',
      valueFormatter: getFormattedDateTimeFromReviewedAt,
      width: 150,
      suppressSizeToFit: true
    },
    {
      field: 'reviewed_by',
      headerName: 'Reviewer',
      cellRenderer: ({ value }: Props) => value || '',
      width: 150,
      suppressSizeToFit: true
    },
    {
      field: 'source',
      headerName: 'Source of FFL',
      cellRenderer: SourceCellRenderer,
      width: 200,
      filter: "agSetColumnFilter",
      filterParams: {
        values: reviewFilters.source.map((item: any) => item.value),
      },
      suppressSizeToFit: true
    },
    {
      field: 'updated_by',
      headerName: 'Uploaded By',
      cellRenderer: ({data}: ValueFormatterParams)=> {
        return data?.source === 'ffl360' ? (data?.updated_by || '-') : '-';
      },
      width: 200,
      suppressSizeToFit: true
    },
    {
      field: 'created_date_time',
      headerName: 'Uploaded Date',
      valueFormatter: getFormattedDateTimeFromCreatedAt,
      width: 250,
      filter: 'agDateColumnFilter',
      filterParams: {
        includeBlanksInRange: true
      },
      suppressSizeToFit: true
    },
    {
      field: 'updated_at',
      headerName: 'Updated Date',
      valueFormatter: ({ value }: { value: string }) => {
        return getFormattedDateTime(value) || '';
      },
      width: 250,
      suppressSizeToFit: true
    },
    {
      field: 'error_code',
      headerName: 'Failure Reason',
      cellRenderer: ({ value }: { value: number }) => {
        if (value) return getFailedStep(value) || '';
        else return '';
      },
      width: 350,
      filter: "agSetColumnFilter",
      filterParams: {
        values: [...reviewFilters.faildSteps.map((item: any) => item.value), "other"],
      },
      suppressSizeToFit: true
    },
    {
      field: 'rejected_reason',
      headerName: 'Rejection Reason',
      cellRenderer: ({ value }: { value: string | null | undefined }) => {
        return normalizeRejectedReasonFromApi(value ?? null) || '';
      },
      width: 350,
      suppressSizeToFit: true
    },
    {
      field: 'note',
      headerName: 'Rejection Note',
      cellRenderer: ({ value }: { value: string }) => {
        return value || '';
      },
      width: 350,
      tooltipField: 'note',
      suppressSizeToFit: true
    },
    {
      field: 'ocr_expiry_date',
      headerName: 'OCR Expiry Date',
      cellRenderer: ({ value, valueFormatted }: Props) => {
        return value || '';
      },
      width: 240,
      suppressSizeToFit: true
    },
    {
      field: 'ez_check_expiry_date',
      headerName: 'EZ Check Expiry Date',
      cellRenderer: ({ value, valueFormatted }: Props) => value || '',
      width: 240,
      suppressSizeToFit: true,
      hide: true
    },
    {
      field: 'reference_id',
      headerName: 'Order ID',
      cellRenderer: ({ value, valueFormatted }: Props) => {
        return value || '';
      },
      width: 240,
      filter: 'agTextColumnFilter',
      suppressSizeToFit: true
    },
    {
      field: 'store_name',
      headerName: 'Store Name',
      cellRenderer: ({ value }: Props) => value || '',
      width: 240,
      suppressSizeToFit: true
    },
    {
      field: 'action_performed_by',
      headerName: 'Manual Review Requester',
      cellRenderer: ActionPerformedByCellRenderer,
      width: 300,
      suppressSizeToFit: true
    },
    {
      field: 'manual_review_requested_at',
      headerName: 'Manual Review Request Date',
      cellRenderer: getFormattedDateTimeFroManualReviewedAt,
      width: 300,
      suppressSizeToFit: true
    },
    {
      field: 'searchText',
      hide: true,
      filter: 'agTextColumnFilter',
      suppressSizeToFit: true
    },
    {
      field: 'is_external',
      headerName: 'Store Type',
      cellRenderer: ({ value, valueFormatted }: Props) => {
        if(['true'].includes(`${value}`)) return 'Live';
        else if(['false'].includes(`${value}`)) return 'Testing';
        else return '';
      },
      width: 200,
      filter: "agSetColumnFilter",
      filterParams: {
        values: [...reviewFilters.storeType.map((item: any) => item.value), 'false'],
      },
      suppressSizeToFit: true
    },
    {
      field: 'unstamped_license_path',
      headerName: 'File Path',
      cellRenderer: ({ value }: Props) => value || '',
      width: 350,
      hide: true,
      suppressSizeToFit: true
    },
  ];
  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      sortable: false
    }),
    []
  );

  const createBolb = async (
    path: string,
    opts?: { bypassHttpCache?: boolean }
  ) => {
    const key = path.split('?')[0].split('#')[0];
    let blob = await s3Utils.getFileBlob(
      key,
      'masterFFLAssets',
      { public: '' },
      { bypassHttpCache: opts?.bypassHttpCache }
    );
    const contentType =
      mime.lookup(key.split('.').pop() as string) ||
      'application/octet-stream';
    blob = new Blob([blob], { type: contentType });
    setLicenseBlob(blob);
  }

  const previewFile = async (path: string) => {
    onLicensePreviewOpen();
    await createBolb(path);
  };

  const downloadFile1 = async (path: string) => {
    setDownloadingFile1(true);
    await s3Utils.getFile(path, "masterFFLAssets", { public: '' });
    setDownloadingFile1(false);
  };
  const downloadFile2 = async (path: string) => {
    setDownloadingFile2(true);
    const res = await s3Utils.getFile(path, "licenseReader", { public: '' });
    console.log('res !!!!!!!', res)
    setDownloadingFile2(false);
  };

  function onCloseLicensePreview() {
    setDownloadingFile1(false);
    setDownloadingFile2(false);
    const blob = new Blob();
    onLicensePreviewClose();
  }

  const gridStyle = useMemo(() => ({ width: '100%' }), []);

  const onRefreshComplete = () => {
    dispatch(setIsRefreshingLOAApproval(false));
  };

  const applyLicenseRowToReviewState = (data: LicenseReview) => {
    const pathOverride = galleryImagePathOverrideByDocId[data.id];
    const merged: LicenseReview =
      pathOverride && !/^blob:/i.test(pathOverride)
        ? { ...data, unstamped_license_path: pathOverride }
        : data;
    const expDate = merged?.ocr_expiry_date || null;
    setOcrExpDate(expDate ? getFormattedDate(trimDate(expDate)) : null);
    const prtDate = merged?.printed_date || null;
    if (prtDate === null) setSignatureDate(null);
    else {
      setSignatureDate(getFormattedDate(trimDate(prtDate)));
    }
    setLicenseReview(merged);
    setReviewInputVal({
      rejectReason:
        normalizeRejectedReasonFromApi(merged?.rejected_reason ?? null) || '',
      note: merged?.note || ''
    });
    setInitialLicenseData(merged);
    const pendingBlob = pendingRotatedBlobByDocIdRef.current.get(data.id);
    if (pendingBlob) {
      setLicenseBlob(pendingBlob);
    } else {
      const thumbBust = galleryThumbnailReloadNonceByDocId[data.id] ?? 0;
      createBolb(merged?.unstamped_license_path || '', {
        bypassHttpCache: thumbBust > 0
      });
    }
    setImageLoadError(false);
  };

  const performRotateForLicenseDoc = async (
    doc: LicenseReview,
    kind: DocumentGridRotateKind
  ) => {
    const sourceKey = normalizeStorageKeyFromApi(
      doc.unstamped_license_path || doc.object_path || ''
    );
    if (!sourceKey) {
      return;
    }
    if (sourceKey.toLowerCase().endsWith('.pdf')) {
      return;
    }
    rotateGenerationByDocIdRef.current[doc.id] =
      (rotateGenerationByDocIdRef.current[doc.id] || 0) + 1;
    const genAtRotate = rotateGenerationByDocIdRef.current[doc.id];

    try {
      let blob = await s3Utils.getFileBlob(
        sourceKey,
        'masterFFLAssets',
        { public: '' },
        { bypassHttpCache: true }
      );
      if (!blob?.size) {
        console.error(
          'Rotate: empty download from S3; check key and masterFFLAssets access.',
          sourceKey
        );
        return;
      }
      const ext = sourceKey.split('.').pop() as string;
      const contentType =
        mime.lookup(ext) || blob.type || 'application/octet-stream';
      blob = new Blob([blob], { type: contentType });
      if (!isRasterImageForBrowserRotation(sourceKey, blob.type)) {
        return;
      }

      const { blob: rotatedBlob, contentType: outContentType } =
        await rotateRasterImageBlob(blob, kind);

      const blobUrl = URL.createObjectURL(rotatedBlob);
      setGalleryImagePathOverrideByDocId((prev) => {
        const prevUrl = prev[doc.id];
        if (prevUrl && /^blob:/i.test(prevUrl)) {
          URL.revokeObjectURL(prevUrl);
        }
        return { ...prev, [doc.id]: blobUrl };
      });
      pendingRotatedBlobByDocIdRef.current.set(doc.id, rotatedBlob);

      if (
        isLicenseReviewModalOpenRef.current &&
        licenseReviewRowIdRef.current === doc.id
      ) {
        setLicenseBlob(rotatedBlob);
        setImageLoadError(false);
      }

      void (async () => {
        try {
          await uploadRotatedRasterToLicenseReader({
            path: sourceKey,
            blob: rotatedBlob,
            contentType: outContentType,
            bucket: 'masterFFLAssets'
          });
          if (rotateGenerationByDocIdRef.current[doc.id] !== genAtRotate) {
            return;
          }
          setGalleryImagePathOverrideByDocId((prev) => {
            const cur = prev[doc.id];
            if (cur && /^blob:/i.test(cur)) {
              const u = cur;
              window.setTimeout(() => URL.revokeObjectURL(u), 5000);
            }
            const { [doc.id]: _removed, ...rest } = prev;
            return rest;
          });
          pendingRotatedBlobByDocIdRef.current.delete(doc.id);
          setGalleryThumbnailReloadNonceByDocId((prev) => {
            const next = (prev[doc.id] ?? 0) + 1;
            persistLicenseGalleryThumbS3Bust(doc.id, next);
            return { ...prev, [doc.id]: next };
          });
          if (
            isLicenseReviewModalOpenRef.current &&
            licenseReviewRowIdRef.current === doc.id
          ) {
            await createBolb(sourceKey, { bypassHttpCache: true });
          }
        } catch (e) {
          console.error(e);
          if (rotateGenerationByDocIdRef.current[doc.id] !== genAtRotate) {
            return;
          }
          setGalleryImagePathOverrideByDocId((prev) => {
            const cur = prev[doc.id];
            if (cur && /^blob:/i.test(cur)) {
              const u = cur;
              window.setTimeout(() => URL.revokeObjectURL(u), 5000);
            }
            const { [doc.id]: _removed, ...rest } = prev;
            return rest;
          });
          pendingRotatedBlobByDocIdRef.current.delete(doc.id);
          if (
            isLicenseReviewModalOpenRef.current &&
            licenseReviewRowIdRef.current === doc.id
          ) {
            if (sourceKey) {
              await createBolb(sourceKey, { bypassHttpCache: true });
            }
          }
        }
      })();
    } catch (e) {
      console.error(e);
    }
  };

  const openLicenseDetailPage = (data: LicenseReview) => {
    applyLicenseRowToReviewState(data);
    window.open(`/admin/license-approval/${data?.id as string}`, '_blank');
  };

  const openRejectFromGalleryRow = (data: LicenseReview) => {
    applyLicenseRowToReviewState(data);
    setReviewConfirmation((prevState: any) => ({
      ...prevState,
      header: 'Reason of Rejection',
      status: 'REJECTED'
    }));
    onOpenReviewConfirmationModal();
  };

  const markLicenseReviewedFromGallery = async (doc: LicenseReview) => {
    try {
      const response = await toggleCertificateMarkedAsReviewedStatus(doc.id);
      if (response.status === 200) {
        dispatch(setIsCertificateStatusUpdated(true));
        reFetchData();
        setDocumentsGalleryRefreshKey((k) => k + 1);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const getLicenseGalleryHoverReviewKind = (
    d: LicenseReview
  ): DocumentCardHoverReviewKind | undefined => {
    return d.reviewed_in_grid_by ? 'done' : 'action';
  };

  const previousFilterModel = useRef<any>(null);

  const applyLicenseTableFilters = useCallback(
    (api: GridApi) => {
      api.setFilterModel({
        ...licenseApprovalFilterModel,
        approval_status: licenseApprovalFilterModel?.status,
        error_code: licenseApprovalFilterModel?.faildSteps,
        is_external: licenseApprovalFilterModel?.storeType,
        isManuallyRequested: licenseApprovalFilterModel?.isManuallyRequested,
        created_date_time:
          licenseApprovalFilterModel?.created_date_time?.dateFrom &&
          licenseApprovalFilterModel?.created_date_time?.dateTo &&
          new Date(licenseApprovalFilterModel.created_date_time.dateFrom) <=
            new Date(licenseApprovalFilterModel.created_date_time.dateTo)
            ? licenseApprovalFilterModel.created_date_time
            : {}
      });

      const newContext: any = {
        customFilters: {
          hideRejection: !hideUnnecessaryRejectionValue
        }
      };
      if (licenseApprovalFilterModel?.isManuallyRequested) {
        previousFilterModel.current = licenseApprovalFilterModel;
        newContext.customFilters.isManuallyRequested =
          licenseApprovalFilterModel.isManuallyRequested.values || [];
      }

      api.setGridOption('context', newContext);
      api.refreshServerSide();
    },
    [licenseApprovalFilterModel, hideUnnecessaryRejectionValue]
  );

  const gridOptions: GridOptions<LicenseReview> = {
    rowModelType: 'serverSide',
    pagination: true,
    paginationPageSize: SERVER_PAGE_LICENSE_APPROVAL_LIST_SIZE,
    cacheBlockSize: SERVER_PAGE_LICENSE_APPROVAL_LIST_SIZE,
    serverSideDatasource: dataSource,
    // // generate a unique ID for each row based on the abuse ID
    getRowId: (params) => {
      return params.data.id;
    },
    getRowStyle: (params) => {
      if (params.data?.action_performed_by !== null &&
        params.data?.action_performed_by !== undefined &&
        params.data?.manual_review_requested_at !== null &&
        params.data?.manual_review_requested_at !== undefined &&
        (params?.data?.approval_status && params?.data?.approval_status?.toLowerCase() === 'pending')
      ) {
        return { 'background-color': 'rgba(255, 0, 0, 0.15)' };
      }
    },
    // column definitions
    columnDefs,
    defaultColDef,

    // row options
    animateRows: true,
    suppressRowClickSelection: true,
    onRowClicked: (event: RowClickedEvent<LicenseReview>) => {
      if (gridRef.current && event.node) {
        gridRef.current.api.deselectAll();
        event.node.setSelected(true);
      }
    },
    onRowDoubleClicked: (event: RowClickedEvent<LicenseReview>) => {
      if (event.data) {
        openLicenseDetailPage(event.data);
      }
    },

    // cell detail options
    suppressCellFocus: true,
    // misc options
    debug: false,
    onRowDataUpdated: (params) => {
      onRefreshComplete()
    },
    onGridReady(params) {
      const api = params.api;
      api.sizeColumnsToFit();
      applyLicenseTableFilters(api);

      const syncNoRowsOverlay = () => {
        const rowCount = api.getDisplayedRowCount();
        if (rowCount) api.hideOverlay();
        else api.showNoRowsOverlay();
      };
      syncNoRowsOverlay();
      api.addEventListener('modelUpdated', syncNoRowsOverlay);
    },
    icons: agGridIconsConfig,
    overlayNoRowsTemplate:
      '<div class="not-found-box"><img src="/images/ui/not-found-icon.png"></img><p class="not-found-box__title">No License Approval found</p><p class="not-found-box__message">Try adjusting your filter to find what you’re looking for</p></div>'
  };

  useEffect(() => {
    if (licenseListView !== 'table') return;
    const api = gridRef.current?.api;
    if (!api) return;
    applyLicenseTableFilters(api);
  }, [licenseListView, applyLicenseTableFilters])

  useEffect(() => {
    if(searchParams.get('transferId') && tabName === 'license-approval'){
      dispatch(setLicenseApprovalSearchText(searchParams.get('transferId') || ''));
      dispatch(
        setLicenseApprovalFilterModel({
          ...licenseApprovalFilterModel,
          status: {
            ...licenseApprovalFilterModel.status,
            values: []
          },
          searchText: {
            ...licenseApprovalFilterModel.searchText,
            filter: searchParams.get('transferId') || ''
          },
        })
      );
    } else {
      dispatch(setLicenseApprovalSearchText(''));
      dispatch(
        setLicenseApprovalFilterModel({
          ...licenseApprovalFilterModel,
          status: {
            ...licenseApprovalFilterModel.status,
            values: ['pending']
          },
          searchText: {
            ...licenseApprovalFilterModel.searchText,
            filter: ''
          },
        })
      );
    }
  }, [searchParams])

  const isSuperAdmin = getIsGlobalAdmin();

  function renderData() {
    return (
      <FFLDetailModal
        id={licenseeId}
        isOpen={isHyperLinkOpen ? isHyperLinkOpen : isLicenseModalOpen}
        onClose={isHyperLinkOpen ? onHyperLinkClose : onLicenseModalClose}
        cognitoGroup={false}
        isSuperAdmin={isSuperAdmin}
      />
    );
  }

  const dateExpPickerRef = useRef<any>(null);
  const dateSigPickerRef = useRef<any>(null);

  const openExpDatePicker = () => {
    if (dateExpPickerRef.current) {
      dateExpPickerRef.current.setOpen(true);
    }
  };

  const openSigDatePicker = () => {
    if (dateSigPickerRef.current) {
      dateSigPickerRef.current.setOpen(true);
    }
  };

  const onDateChange = async (date: Date, fieldName: string) => {
    if (fieldName === 'ocr_expiry_date') {
      setOcrExpDate(getFormattedDate(date.toDateString()));
      setLicenseReview((prevState: any) => ({
        ...prevState,
        errors: { ...(prevState.errors || {}), [fieldName]: '' }
      }))
    } else {
      setSignatureDate(getFormattedDate(date.toDateString()));
    }
  }

  const onOpenCertificateStatus = async (status: string) => {
    if(!licenseReview?.unstamped_license_path && status === 'APPROVED') {
      setInvalidCertificate((prev: any) => ({ ...prev, header: "Invalid FFL Certificate" }));
      return onOpenInvalidCertificateModal();
    }

    if (status === "APPROVED") {
      await validateField('full_license_key', licenseReview?.full_license_key ? licenseReview.full_license_key as string : "");
      if (!ocrExpDate) {
        setLicenseReview((prevState: any) => ({
          ...prevState,
          errors: { ...(prevState.errors || {}), 'ocr_expiry_date': 'Expiry date is required.' }
        }))
        return
      }
      if (
        ((initialLicenseData?.full_license_key == null || !regexFullLicense.test(licenseReview?.full_license_key as string)) && (!licenseReview?.full_license_key || licenseReview?.errors && licenseReview.errors.full_license_key)) ||
        (licenseReview?.printed_name && licenseReview?.errors && licenseReview.errors.printed_name) ||
        (licenseReview?.position_title && licenseReview?.errors && licenseReview.errors.position_title)
      ) {
        return
      }
      setReviewConfirmation((prevState: any) => ({
        ...prevState,
        header: "Reason of Approval",
        status: status
      }))
    } else {
      setReviewConfirmation((prevState: any) => ({
        ...prevState,
        header: "Reason of Rejection",
        status: status
      }))
    }
    onOpenReviewConfirmationModal();
  }

  const updateCertificateStatus = async (status: string, isSaveClicked: boolean = false, isEmailIgnored:boolean = false) => {
    try {
      setReviewConfirmation((prevState: ReviewConfirmation) => ({
        ...prevState,
        isConfirmBtnLoading: !isSaveClicked ? true : isSaveClicked,
        isCancelBtnDisable: true,
        isSaveBtnLoading: isSaveClicked,
      }));
      const response = await updateLicenseApprovalStatus(
        licenseReview?.id as string,
        status,
        licenseReview?.full_license_key || "",
        licenseReview?.printed_name,
        licenseReview?.position_title,
        ocrExpDate,
        signatureDate,
        licenseReview?.premise_address,
        licenseReview?.mailing_address,
        reviewConfirmation?.rejectedReason,
        reviewConfirmation?.note,
        isEmailIgnored
      );
      if (response.status === 200) {
        dispatch(setIsCertificateStatusUpdated(true));
        reFetchData();
        setDocumentsGalleryRefreshKey((k) => k + 1);
      }
      closeReviewConfirmationModal();
      onClose();
    } catch (err: any) {
      console.log('ERROR', err);
      if (
        err &&
        err?.response &&
        err.response?.status == 404 &&
        err.response?.data &&
        err.response.data?.Error
      ) {
        setLicenseReview((prevState: any) => ({
          ...prevState,
          errors: { ...(prevState.errors || {}), 'full_license_key': err.response.data?.Error }
        }))
      }
    } finally {
      setReviewConfirmation((prevState: ReviewConfirmation) => ({
        ...prevState,
        isConfirmBtnLoading: false,
        isCancelBtnDisable: false,
        isSaveBtnLoading: false,
      }));
    }
  };

  const validateField = async (name: string, value: string | Date) => {
    try {
      await licenseApprovalValidation.validateAt(name, { [name]: value });
      setLicenseReview((prevState: any) => ({
        ...prevState,
        errors: { ...(prevState.errors || {}), [name]: '' }
      }));
    } catch (error: any) {
      if (error.errors && error.errors.length > 0) {
        setLicenseReview((prevState: any) => ({
          ...prevState,
          errors: { ...(prevState.errors || {}), [name]: error.errors[0] }
        }));
      }
    }
  };

  const onChangeInputs = async (value: string, fieldName: string) => {
    await validateField(fieldName, value as string);
    setLicenseReview((prevState: any) => ({
      ...prevState,
      [fieldName]: value
    }));
  };

  const handleChange = async (event: any) => {
    setReviewConfirmation((prevState: ReviewConfirmation) => ({
      ...prevState,
      rejectedReason: event.target.value
    }));
  };

  const handleTextChange = async (event: any) => {
    const value = event.target.value;
    try {
      await textareaLengthValidation.validateAt('textarea', { 'textarea': value });
      setReviewConfirmation((prevState: ReviewConfirmation) => ({
        ...prevState,
        note: event.target.value,
        error: ''
      }));
    } catch (error: any) {
      if (error.errors && error.errors.length > 0) {
        setReviewConfirmation((prevState: ReviewConfirmation) => ({
          ...prevState,
          error: error.errors[0]
        }));
      }
    }
  };
  const [dealer_id, setDealerId] = useState<string>('');

  const downloadIcon1 = () => {
    return (
      <>
        {downloadingFile1 ? (
          <button className="license_details__download-btn download-license-height">
            <Spinner size="sm" />
          </button>
        ) : (
          <Box
            display={'inline'}
            position={'relative'}
            top={'6px'}
            pl={2}
          >
          <Tooltip
            hasArrow
            placement="top"
            label="Download JPEG Certificate"
            color={'black'}
            bg={'white'}
          >
            <button
              className="license_details__download-btn download-license-height"
              onClick={() => downloadFile1(licenseReview?.unstamped_license_path as string)}
              disabled={!licenseReview?.unstamped_license_path}
            >
              <Image src="/images/ui/download.svg" />
            </button>
          </Tooltip>
          </Box>
        )}
      </>
    );
  };

  const downloadIcon2 = () => {
    return (
      <>
        {downloadingFile2 ? (
          <button className="license_details__download-btn download-license-height">
            <Spinner size="sm" />
          </button>
        ) : (
          <Box
            display={'inline'}
            position={'relative'}
            top={'6px'}
            pl={2}
          >
          <Tooltip
            hasArrow
            placement="top"
            label="Download Original Certificate"
            color={'black'}
            bg={'white'}
          >
            <button
              className="license_details__download-btn download-license-height"
              onClick={() => downloadFile2(licenseReview?.object_path as string)}
              disabled={!licenseReview?.object_path}
            >
              <Image src="/images/ui/download.svg" />
            </button>
          </Tooltip>
          </Box>
        )}
      </>
    );
  };

  const closeReviewConfirmationModal = () => {
    onCloseReviewConfirmationModal();
    setReviewConfirmation({
      header: "",
      isCancelBtnDisable: false,
      isConfirmBtnDisable: false,
      isConfirmBtnLoading: false,
      isSaveBtnLoading: false,
      rejectedReason: "",
      note: "",
      error: "",
      status: "",
    });
  }

  const disableConfirmButton = () => {
    if(reviewConfirmation.error || (reviewConfirmation.status === "REJECTED" && !reviewConfirmation.rejectedReason)){
      return true;
    }
    return false;
  }
  let imgUrl = '';
  if(licenseBlob && licenseBlob?.type){
    imgUrl = URL.createObjectURL(licenseBlob);
  }

  const onSave = async() => {
    await validateField('full_license_key', licenseReview?.full_license_key ? licenseReview.full_license_key as string : "");
    if (!ocrExpDate) {
      setLicenseReview((prevState: any) => ({
        ...prevState,
        errors: { ...(prevState.errors || {}), 'ocr_expiry_date': 'Expiry date is required.' }
      }))
      return
    }
    updateCertificateStatus(licenseReview?.approval_status ? licenseReview?.approval_status.toUpperCase() : "PENDING", true)
  }

  useEffect(() => {
    if (isOpen) {
      dispatch(setDocumentsRequiredDetails({}));
    }
  }, [isOpen]); 

  useEffect(()=> {
    setDealerId(licenseReview?.dealer_id || "");
  },[licenseReview?.dealer_id] );

  useEffect(() => {
    const licenseKey = licenseReview?.full_license_key?.replace(/[*_-]/g, '');
    const len = licenseKey?.length;
    if (licenseKey && licenseKey.length === 15 && isOpen == true) {
      dispatch(callToGetLicenseRecordDetails({ id: licenseReview?.full_license_key }));
    } else {
      dispatch(setDocumentsRequiredDetails({}));
      setDealerId('');
    }
  }, [licenseReview?.full_license_key, isOpen]);
  const validLicenseRecordDetails = getValidLicenseRecord()

  useEffect(() => {
    if (validLicenseRecordDetails?.dealerId && validLicenseRecordDetails?.fullLicenseKeys?.[0]) {
      dispatch(callToGetDocumentStatus({ id: validLicenseRecordDetails?.fullLicenseKeys?.[0] }));
      setDealerId(validLicenseRecordDetails?.dealerId);
    }
    else{
      setLicenseReview((prevState: any) => ({
        ...prevState,
        errors: { ...(prevState?.errors || {}), 'full_license_key': 'Enter valid license number' }
      }))
      setDealerId('');
    } 
  }, [validLicenseRecordDetails]);
  const isDealerDetailsLoading = getIsDealerDetailsLoading();

  const documentsRequiredDetails = getDocumentsRequiredDetails()

  return (
    <>
      {renderData()}
      <Box className='approval-header-wrapper'>
         <Heading size={'md'}>License Approval</Heading>
         <Box className='a-view-toggle-wrapper'>
           <Text className='view-toggle-text'>View :</Text>
           <Flex
             className="license-view-toggle"
             role="group"
             aria-label="License list layout"
           >
             <Box
               as="button"
               type="button"
               className={`license-view-toggle__segment${
                 licenseListView === 'table'
                   ? ' license-view-toggle__segment--active'
                   : ''
               }`}
               onClick={() => setLicenseListView('table')}
               aria-pressed={licenseListView === 'table'}
             >
               <LicenseTableViewIcon
                 color={
                   licenseListView === 'table' ? '#0C1E46' : '#4A5565'
                 }
               />
               <span className="license-view-toggle__label">Table</span>
             </Box>
             <Box
               as="button"
               type="button"
               className={`license-view-toggle__segment${
                 licenseListView === 'gallery'
                   ? ' license-view-toggle__segment--active'
                   : ''
               }`}
               onClick={() => setLicenseListView('gallery')}
               aria-pressed={licenseListView === 'gallery'}
             >
               <LicenseGalleryViewIcon
                 color={
                   licenseListView === 'gallery' ? '#0C1E46' : '#4A5565'
                 }
               />
               <span className="license-view-toggle__label">Gallery</span>
             </Box>
           </Flex>
         </Box>
      </Box>
      {licenseListView === 'table' ? (
        <Box style={gridStyle} className={className}>
          <AgGridReact
            className={'ag-theme-alpine'}
            ref={gridRef}
            gridOptions={gridOptions}
            rowSelection="single"
          />
        </Box>
      ) : (
        <LicenseDocumentsGridView
          onDocumentClick={openLicenseDetailPage}
          onHoverReject={openRejectFromGalleryRow}
          onHoverRotate={performRotateForLicenseDoc}
          getHoverReviewKind={getLicenseGalleryHoverReviewKind}
          onHoverReview={markLicenseReviewedFromGallery}
          galleryThumbnailUrlByDocId={galleryImagePathOverrideByDocId}
          galleryThumbnailReloadNonceByDocId={galleryThumbnailReloadNonceByDocId}
          refreshTrigger={documentsGalleryRefreshKey}
        />
      )}
      <ModalComponent onClose={() => {
        setLicenseReview((prevState: any) => ({
          ...prevState,
          full_license_key: null,
          printed_name: null,
          position_title: null,
          premise_address: null,
          mailing_address: null,
          errors: {}
        }))
        setOcrExpDate(null);
        setSignatureDate(null);
        onClose();
      }}
        isOpen={isOpen}
        title={<>License Approval</>}
        variant={2}
        className="license-approval-modal"
        padding={'25px !important'}
        showCloseIcon={false}
        footer={
          <>
          <Button 
              background= "#0C1E46"
               colorScheme= "blue"
              padding="8px 20px"
              marginRight="20px"
              isLoading={reviewConfirmation.isSaveBtnLoading}
              sx={{outline: 'none !important'}}
              isDisabled={
                (licenseReview &&
                licenseReview?.errors &&
                Object.values(licenseReview?.errors).some((val) => val !== '') ? true : false)
              }
              onClick={() => {onSave()}}
            >
              Save
            </Button>
            <Button 
              {...(checkStatus(licenseReview?.approval_status, "approved") && {leftIcon : <Image src="/images/ui/success.svg" />, isDisabled: true })}
              {...(!checkStatus(licenseReview?.approval_status, "pending") ? {colorScheme: "green", variant: 'outline'} : {background: "#0C1E46", colorScheme: "blue" })}
              padding="8px 20px"
              marginRight="20px"
              sx={{outline: 'none !important'}}
              onClick={() => { onOpenCertificateStatus("APPROVED") }}
            >
              {!checkStatus(licenseReview?.approval_status, "approved") ? "Approve" : "Approved"}
            </Button>
            <Button
              {...(checkStatus(licenseReview?.approval_status, "rejected") && {leftIcon : <Image src="/images/ui/reject.svg" /> })}
              {...(!checkStatus(licenseReview?.approval_status, "pending") ? {colorScheme: "red", border: "1px solid red", padding: "8px 20px"} : {background: "#ffffff", colorScheme: "blue", color: "#0C1E46", border: "1px solid #0C1E46", padding: "8px 11px" })}
              onClick={() => { onOpenCertificateStatus("REJECTED") }}
              marginRight="20px"
              sx={{outline: 'none !important'}}
              {...(!checkStatus(licenseReview?.approval_status, "pending") && {variant: 'outline'})}
              {...(checkStatus(licenseReview?.approval_status, "rejected") && {isDisabled: true})}
            >
              {!checkStatus(licenseReview?.approval_status, "rejected") ? "Reject" : "Rejected"}
            </Button>
            <Button
              colorScheme="blue"
              background="#ffffff"
              color="#0C1E46"
              border="1px solid #0C1E46"
              padding="8px 11px"
              sx={{outline: 'none !important'}}
              onClick={() => {
                onClose();
                const blob = new Blob();
                setLicenseBlob(blob);
              }}
            >
              Cancel
            </Button>
          </>
        }
      >
        <div className='approval-model-body'>
          <Grid templateColumns={{ base: '1fr', xl: '30% 70%' }}>
            <GridItem pb={6}>
              <TableContainer>
                <Table>
                  <Tbody>
                    <TableElement noBorder={true} label={
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  License Number
                                  {(dealer_id) && (
                                    <img 
                                      src="/images/ui/blue-link.svg" 
                                      alt="License" 
                                      onClick={(e) => { 
                                        e.stopPropagation(); 
                                        console.log({dealer_id: dealer_id});
                                        window.open(`/dealers-search/${dealer_id}`, '_blank');  
                                      }} 
                                      style={{ 
                                        width: '16px', 
                                        height: '16px', 
                                        cursor: 'pointer'
                                      }} 
                                    />
                                  )}
                        </div>
                    }>
                      <>
                      <div style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
                        <InputMask
                            id="formattedInput"
                            mask="9-99-999-99-9a-99999"
                            placeholder="X-XX-XXX-XX-XX-XXXXX"
                            value={licenseReview?.full_license_key || ""}
                            onChange={(event: any) =>
                                onChangeInputs(
                                event.target.value,
                                'full_license_key'
                                )
                            }
                        >
                            {(inputProps: any) => <input 
                                                    {...inputProps} 
                                                    type="text" 
                                                    className='sot-customize-input' 
                                                    />
                                                  }
                        </InputMask>
                  { isDealerDetailsLoading && (
                      <Spinner 
                        size="sm" 
                        style={{ 
                          position: 'absolute',
                          right: '8px',
                          top: '25%',
                          transform: 'translateY(-50%)',
                          pointerEvents: 'none'
                        }}
                      />
                    )}
                      
                    </div>
                        {licenseReview?.errors && licenseReview?.errors?.full_license_key ?  
                          <div className="">
                            <div className="edit-error">
                              {licenseReview.errors && licenseReview?.errors.full_license_key && (
                                <>* {licenseReview?.errors.full_license_key}</>
                              )}
                            </div>
                          </div> : ""
                        }
                      </>
                    </TableElement>
                    <TableElement label={'Expiry Date'}>
                      <Box>
                        <>
                          <FlatDatePickr
                              label=""
                              value={fixTimezoneOffset(ocrExpDate)}
                              onDateChange={(date: Date) => onDateChange(date, 'ocr_expiry_date')}
                              options={{ minDate: new Date() }}
                              classNameValue="transfer-form__input order-date-picker"
                          />
                          {licenseReview?.errors && licenseReview?.errors?.ocr_expiry_date ?
                            <div className="">
                              <div className="edit-error">
                                {licenseReview.errors && licenseReview?.errors.ocr_expiry_date && (
                                  <>* {licenseReview?.errors.ocr_expiry_date}</>
                                )}
                              </div>
                            </div> : ""
                          }
                        </>
                      </Box>
                    </TableElement>
                    <TableElement label={'Premises Address'}>
                      <>
                        <Textarea
                          className="license-review__input contact-detail-form__contact-name"
                          placeholder="Enter Premises Address"
                          value={licenseReview?.premise_address || ""}
                          onChange={(event) => {
                              onChangeInputs(
                                event.target.value,
                                'premise_address'
                              );
                              setTextareaEvent(event.target.selectionStart);
                            }
                          }
                          ref={premiseAddress as any}
                        />
                      </>
                    </TableElement>
                    <TableElement label={'Mailing Address'}>
                      <>
                        <Textarea
                          className="license-review__input contact-detail-form__contact-name"
                          placeholder="Enter Mailing Address"
                          value={licenseReview?.mailing_address || ""}
                          onChange={(event) => {
                              onChangeInputs(
                                event.target.value,
                                'mailing_address'
                              );
                              setTextareaEvent(event.target.selectionStart);
                            }
                          }
                          ref={mailingAddress as any}
                        />
                      </>
                    </TableElement>
                    <TableElement  noBorder={true} label={'Name'}>
                      <>
                        <ControlledInput
                          className="license-review__input contact-detail-form__contact-name"
                          type="text"
                          placeholder="Enter Name"
                          value={licenseReview?.printed_name || ""}
                          onChange={(event) =>
                            onChangeInputs(
                              event.target.value,
                              'printed_name'
                            )
                          }
                        />
                        {licenseReview?.errors && licenseReview?.errors?.printed_name ?
                          <div className="">
                            <div className="edit-error">
                              {licenseReview.errors && licenseReview?.errors.printed_name && (
                                <>* {licenseReview?.errors.printed_name}</>
                              )}
                            </div>
                          </div> : ""
                        }
                      </>
                    </TableElement>
                    <TableElement label={'Title'}>
                      <>
                        <ControlledInput
                          className="license-review__input contact-detail-form__contact-name"
                          type="text"
                          placeholder="Enter Title"
                          value={licenseReview?.position_title || ""}
                          onChange={(event) =>
                            onChangeInputs(
                              event.target.value,
                              'position_title'
                            )
                          }
                        />
                        {licenseReview?.errors && licenseReview?.errors?.position_title ?
                          <div className="">
                            <div className="edit-error">
                              {licenseReview.errors && licenseReview?.errors.position_title && (
                                <>* {licenseReview?.errors.position_title}</>
                              )}
                            </div>
                          </div> : ""
                        }
                      </>
                    </TableElement>
                    <TableElement  label={'Signature Date'}>
                      <Box>
                        <FlatDatePickr
                            label=""
                            value={signatureDate}
                            onDateChange={(date: Date) => onDateChange(date, 'printed_date')}
                            classNameValue="transfer-form__input order-date-picker"
                        />
                      </Box>
                    </TableElement>
                    <TableElement label={'Status'}>
                      {licenseReview?.status === '0' ? 'Success' : 'Failed'}
                    </TableElement>
                    <TableElement label={'Failed at Step'}>
                      {getFailedStep(licenseReview?.error_code || null)}
                    </TableElement>
                    <TableElement label={'Uploaded Time'}>
                      {licenseReview?.created_date_time
                        ? getFormattedDateTime(new Date(licenseReview?.created_date_time)) : ''}
                    </TableElement>
                    {licenseReview?.action_performed_by && (
                    <TableElement label={'Manual Review Requested By'}>
                      {licenseReview?.action_performed_by
                        ? getFormattedTextForManualReview(licenseReview?.action_performed_by, licenseReview?.manual_review_requested_by) : ''}
                    </TableElement>
                    )}
                    {licenseReview?.manual_review_requested_at && (
                    <TableElement label={'Manual Review Requested At'}>
                      {licenseReview?.manual_review_requested_at
                        ? getFormattedDateTime(licenseReview?.manual_review_requested_at  ) : ''}
                    </TableElement>
                    )}
                    {/* <TableElement label={'EZ Check Expiry Date'}>
                      {licenseReview?.ez_check_expiry_date || 'NONE'}
                    </TableElement> */}
                    <TableElement label={'Rejection Reason'}>
                      <>
                        <Select
                            placeholder='Select reason*' 
                            my={3}
                            onChange={(event) =>{
                              setReviewConfirmation({
                                ...reviewConfirmation,
                                rejectedReason: event.target.value,
                              })
                              setReviewInputVal({...reviewInputVal, rejectReason: event.target.value})
                            }}
                            disabled={!checkStatus(licenseReview?.approval_status, "rejected")}
                            required
                        >{
                            Object.entries(reviewRejectedReasonsFflLoa).map(([key, val]) => <option value={val as string} selected={val === reviewInputVal?.rejectReason}>{val as string}</option>)
                          }
                        </Select>
                      </>
                    </TableElement>
                    {!checkStatus(licenseReview?.approval_status, "pending") && <TableElement label={'Note'}>
                      <>
                        <Textarea
                          className="license-review__input contact-detail-form__contact-name"
                          value={reviewInputVal?.note || ""}
                          onChange={(e) => {
                              setReviewConfirmation({
                                ...reviewConfirmation,
                                note: e.target.value,
                              })
                              setReviewInputVal({
                                ...reviewInputVal,
                                note: e.target.value,
                              })
                          }}
                        // disabled={true}
                        />
                      </>
                    </TableElement>}
                  </Tbody>
                </Table>
              </TableContainer>
            </GridItem>
            <Grid templateRows={{ base: '60px auto' }}>
              <GridItem h={'50px'} mb={2}>
                <TableContainer>
                  <Table>
                    <Tbody display='flex' justifyContent='space-between' alignItems='center'>
                      <Box className="transfer_flex_row" sx={{ gap: '8px', marginTop: '8px' }}>
                        <Box className={`doc_box ${documentsRequiredDetails.hasValidCertificate ? 'doc_active' : ''}`}>FFL License</Box>
                        <Box className={`doc_box ${documentsRequiredDetails?.hasValidSOT ? 'doc_active' : ''}`}>SOT</Box>
                        <Box className={`doc_box ${documentsRequiredDetails?.hasValidLOA ? 'doc_active' : ''}`}>LOA</Box>
                      </Box>
                      {/* Right side — downloads + rotate (raster optimized image only) */}
                      <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
                        {(!imageLoadError && licenseReview?.unstamped_license_path) ? (<TableElement
                            label={'Optimized File'}
                            className="license-file-path"
                            renderFunction={downloadIcon1}
                          />
                        ) :  ( <div></div>)}
                        <TableElement
                          label={'Original File'}
                          className="license-file-path"
                          renderFunction={downloadIcon2}
                        ></TableElement>
                        {licenseReview?.unstamped_license_path &&
                        licenseReview.unstamped_license_path.split('.').pop()?.toLowerCase() !== 'pdf' &&
                        isRasterImageForBrowserRotation(
                          licenseReview.unstamped_license_path,
                          licenseBlob?.type
                        ) ? (
                          <Menu>
                            <MenuButton
                              as={Button}
                              size="sm"
                              variant="outline"
                              colorScheme="blue"
                            >
                              Rotate
                            </MenuButton>
                            <MenuList zIndex={2000}>
                              <MenuItem
                                onClick={() =>
                                  licenseReview &&
                                  performRotateForLicenseDoc(licenseReview, 'ccw90')
                                }
                              >
                                90° counter-clockwise
                              </MenuItem>
                              <MenuItem
                                onClick={() =>
                                  licenseReview &&
                                  performRotateForLicenseDoc(licenseReview, 'cw90')
                                }
                              >
                                90° clockwise
                              </MenuItem>
                              <MenuItem
                                onClick={() =>
                                  licenseReview &&
                                  performRotateForLicenseDoc(licenseReview, 'deg180')
                                }
                              >
                                180°
                              </MenuItem>
                            </MenuList>
                          </Menu>
                        ) : null}
                      </div>
                    </Tbody>
                  </Table>
                </TableContainer>
              </GridItem>
              <GridItem
                  w={"100%"}
                  h={'auto'}
              >
                <TableContainer display='flex' justifyContent='center' alignItems={"center"}>
                  <Table>
                    <Tbody>
                          <Box w='100%' className='license-image'>
                              {licenseReview?.unstamped_license_path && !(licenseReview?.unstamped_license_path.split('.').pop() === 'pdf') && <MapInteractionCSS
                                showControls
                                defaultValue={{
                                  scale: 1,
                                  translation: { x: 0, y: 20 }
                                }}
                                minScale={0.5}
                                maxScale={3}
                                translationBounds={{
                                  xMax: 400,
                                  yMax: 200
                                }}
                                btnClass={'map-interection-buttons'}
                              >
                                <img src={imgUrl || ''} width='100%' height="" alt={imageLoadError ? " " : 'Loading...'} className={""}
                                  onLoad={() => {
                                    imgUrl && setImageLoadError(false);
                                  }}
                                  onError={() => {
                                    imgUrl && setImageLoadError(true);
                                  }}
                                />
                              </MapInteractionCSS>}
                          </Box>
                    </Tbody>
                  </Table>
                </TableContainer>
              </GridItem>

              <GridItem
                  display={!licenseBlob?.size ? "flex" : ""}
                  justifyContent={"center"}
                  alignItems={"start"}
                  w={"100%"}
                  h={'auto'}
              >
                <TableContainer>
                  <Table>
                    <Tbody>
                          <Box w='100%'>
                             {licenseReview?.unstamped_license_path && (licenseReview?.unstamped_license_path.split('.').pop() === 'pdf') && 
                              <ShowPreview blob={licenseBlob} id={"license-preview-iframe"} className={"license-preview-loader"} />
                             }
                          </Box>
                    </Tbody>
                  </Table>
                </TableContainer>
              </GridItem>
            </Grid>
          </Grid>
        </div>
      </ModalComponent>
      <ReviewConfirmationModal
        isOpen={isReviewConfirmationMadalOpen}
        onClose={() => {
          setReviewConfirmation({
            ...reviewConfirmation,
            rejectedReason: '',
            note: '',
            error: '',
          })
          closeReviewConfirmationModal()
        }}
        modalHeader={reviewConfirmation.header}
        isCancelBtnDisable={reviewConfirmation.isCancelBtnDisable}
        isConfirmBtnDisable={disableConfirmButton()}
        isConfirmBtnLoading={reviewConfirmation.isConfirmBtnLoading}
        onClickConfirmBtn={(isEmailIgnored) => { updateCertificateStatus(reviewConfirmation.status, false, isEmailIgnored) }}
        fields={reviewRejectedReasonsFflLoa}
        selectedValue={reviewConfirmation.rejectedReason}
        handleChange={handleChange}
        handleTextChange={handleTextChange}
        hideDropdown={reviewConfirmation.status !== "APPROVED"}
        error={reviewConfirmation.error}
        approvalDetails={licenseReview}
      />
      <InvalidCertificateModal
        isOpen={isInvalidCertificateMadalOpen}
        onClose={onClosInvalidCertificateModal}
        modalHeader={invalidCertificate.header}
        note={invalidCertificate.note}
      />
      <PreviewLicense
        isOpen={isLicensePreviewOpen}
        onClose={onCloseLicensePreview}
        onOpen={onLicensePreviewOpen}
        blob={licenseBlob}
        title={'License Preview'}
        filePath={licenseReview?.unstamped_license_path as string}
      />
    </>
  );
}

export default LicenseReviewTable;
